import type { InfiniteData } from "@tanstack/react-query";
import get from "lodash/get";
import isArray from "lodash/isArray";
import type { Paths } from "type-fest";
import { firstMetaPagination } from "./constant";

export function infiniteDataAddFirst<T, D>(
	data: D,
	target: keyof T,
): InfiniteData<T, unknown> {
	return {
		pageParams: [1],
		pages: [
			{
				[target]: [data],
				meta: firstMetaPagination,
			} as T,
		],
	};
}

export function infiniteDataAdd<
	T,
	K extends keyof T,
	V extends NonNullable<T[K]> extends Array<infer U> ? U : never,
>(
	origin: InfiniteData<T, unknown> | undefined,
	dataTarget: K,
	data: V,
	addToStart = false,
): InfiniteData<T, unknown> {
	if (!origin) return infiniteDataAddFirst<T, V>(data, dataTarget);
	return {
		...origin,
		pages: origin.pages.map((page, index) => {
			if (index === 0) {
				const targetArray = Array.isArray(page[dataTarget])
					? page[dataTarget]
					: [];
				return {
					...page,
					[dataTarget]: addToStart
						? ([data, ...targetArray] as unknown as T[keyof T])
						: ([...targetArray, data] as unknown as T[keyof T]),
					meta: {
						// @ts-expect-error
						...page?.meta,
						// @ts-expect-error
						total_count: Math.max(0, (page?.meta?.total_count ?? 0) + 1),
					},
				};
			}
			return page;
		}),
	};
}

export function infiniteDataReplace<
	T,
	K extends keyof T,
	V extends NonNullable<T[K]> extends Array<infer U> ? U : never,
>(
	origin: InfiniteData<T, unknown> | undefined,
	dataTarget: K,
	matchKey: Paths<V>,
	matchValue: any,
	newData: V,
): InfiniteData<T, unknown> {
	if (!origin) return infiniteDataAddFirst<T, V>(newData, dataTarget);
	return {
		...origin,
		pages: origin.pages.map((page) => ({
			...page,
			[dataTarget]: isArray(page[dataTarget])
				? (page[dataTarget] as V[]).map((item) =>
						get(item, String(matchKey)) === matchValue ? newData : item,
					)
				: page[dataTarget],
		})),
	};
}

export function infiniteDataRemove<
	T,
	K extends keyof T,
	V extends NonNullable<T[K]> extends Array<infer U> ? U : never,
>(
	origin: InfiniteData<T, unknown> | undefined,
	dataTarget: K,
	matchKey: Paths<V>,
	matchValue: any,
): InfiniteData<T, unknown> {
	if (!origin) return origin as unknown as InfiniteData<T, unknown>;
	return {
		...origin,
		pages: origin.pages.map((page) => {
			if (isArray(page[dataTarget])) {
				const filtered = (page[dataTarget] as V[]).filter(
					(item) => get(item, String(matchKey)) !== matchValue,
				);
				const removedCount = (page[dataTarget] as V[]).length - filtered.length;

				return {
					...page,
					[dataTarget]: filtered as unknown as T[keyof T],
					meta: {
						// @ts-expect-error
						...page?.meta,
						total_count: Math.max(
							0,
							// @ts-expect-error
							(page?.meta?.total_count ?? 0) - removedCount,
						),
					},
				};
			}
			return page;
		}),
	};
}
